// ConvoCX unified inbox — three-pane workspace page (CON-845, shell CON-853).
// Composes the queue, thread, composer, details, command bar, and toasts as the
// routed content of the app shell. The shell owns the top nav + side nav.

import { useCallback, useState } from 'react';
import { useAuth } from './auth';
import { useInbox } from './useInbox';
import { ConversationQueue } from './ConversationQueue';
import { ConversationThread } from './ConversationThread';
import { ConversationDetails } from './ConversationDetails';
import { CommandBar } from './CommandBar';
import { Notifications } from './Notifications';
import './inbox.css';

export type ThemeMode = 'dark' | 'light';

export interface InboxPageProps {
  mode: ThemeMode;
  onToggleMode: () => void;
}

export function InboxPage({ mode }: InboxPageProps) {
  const { token } = useAuth();
  const inbox = useInbox(token);
  const [sending, setSending] = useState(false);

  const { selectedId, selectedConversation } = inbox;

  const focusSearch = useCallback(() => {
    document.querySelector<HTMLInputElement>('[data-testid="inbox-queue"] input[type="text"]')?.focus();
  }, []);

  const handleSend = useCallback(async (body: string) => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await inbox.sendMessage(body);
    } finally {
      setSending(false);
    }
  }, [inbox]);

  const handleAssign = useCallback((userId: string | null) => {
    if (!selectedId) return;
    inbox.updateConversation(selectedId, { assigned_to: userId });
  }, [selectedId, inbox]);

  const handleStatusChange = useCallback((status: import('./types').ConversationStatus) => {
    if (!selectedId) return;
    inbox.updateConversation(selectedId, { status });
  }, [selectedId, inbox]);

  const commandActions = {
    setStatusFilter: (s: import('./useInbox').StatusFilter) => inbox.setFilters((f) => ({ ...f, statuses: s === 'all' ? [] : [s] })),
    setChannelFilter: (c: import('./useInbox').ChannelFilter) => inbox.setFilters((f) => ({ ...f, channels: c === 'all' ? [] : [c] })),
    selectConversation: inbox.selectConversation,
    setStatus: handleStatusChange,
    assignTo: handleAssign,
    toggleStar: () => selectedId && inbox.toggleStar(selectedId),
    toggleArchive: () => selectedId && inbox.toggleArchive(selectedId),
    focusSearch,
  };

  return (
    <>
      <div
        className={`ccx-inbox${selectedId ? ' has-thread' : ''}`}
        data-theme-test={mode}
        data-testid="ccx-inbox"
      >
        <ConversationQueue
          conversations={inbox.conversations}
          loading={inbox.loading}
          error={inbox.error}
          selectedId={selectedId}
          filters={inbox.filters}
          onFiltersChange={(f) => inbox.setFilters((prev) => ({ ...prev, ...f }))}
          onSelect={inbox.selectConversation}
          onRetry={inbox.fetchConversations}
        />

        <ConversationThread
          conversation={selectedConversation}
          messages={inbox.messages}
          loading={inbox.messagesLoading}
          sending={sending}
          onSend={handleSend}
          onBack={() => inbox.selectConversation(null)}
          canSend={!!token}
          onToggleStar={() => selectedId && inbox.toggleStar(selectedId)}
          onToggleArchive={() => selectedId && inbox.toggleArchive(selectedId)}
        />

        <ConversationDetails
          conversation={selectedConversation}
          team={inbox.team}
          labels={inbox.labels}
          currentUserId={null}
          onStatusChange={handleStatusChange}
          onAssign={handleAssign}
          onToggleLabel={(label) => selectedId && (selectedConversation?.labels?.some((l) => l.id === label.id)
            ? inbox.removeLabel(selectedId, label.id)
            : inbox.assignLabel(selectedId, label))}
          onToggleStar={() => selectedId && inbox.toggleStar(selectedId)}
          onToggleArchive={() => selectedId && inbox.toggleArchive(selectedId)}
        />
      </div>

      <CommandBar
        conversations={inbox.conversations}
        selectedId={selectedId}
        team={inbox.team}
        actions={commandActions}
        enabled={!!token}
      />

      <Notifications
        notifications={inbox.notifications}
        onDismiss={(id) => inbox.setNotifications((prev) => prev.filter((n) => n.id !== id))}
      />
    </>
  );
}